import './App.css';
import {useCallback, useEffect, useState} from 'react';
import {apiFetch, getStoredUsername, setStoredUsername, setStoredUserId} from './api';
import Header from './components/Header';
import Footer from './components/Footer';
import {ToastProvider} from './components/Toast';
import LandingPage from './components/LandingPage';
import LoginScreen from './components/LoginScreen';
import RegisterScreen from './components/RegisterScreen';
import ForgotPasswordScreen from './components/ForgotPasswordScreen';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import ChecklistContainer from './components/ChecklistContainer';
import AccountSettingsPage from './components/AccountSettingsPage';
import Spinner from './components/Spinner';

export default function App() {
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
    const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading
    const [username, setUsername] = useState(getStoredUsername);
    const [view, setView] = useState('checklists');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentViewLabel, setCurrentViewLabel] = useState('All');

    const toggleSidebar = useCallback(() => setSidebarOpen(prev => !prev), []);

    const toggleTheme = useCallback(() => setTheme(t => {
        const next = t === 'dark' ? 'light' : t === 'light' ? 'minimal' : t === 'minimal' ? 'github' : 'dark';
        localStorage.setItem('theme', next);
        return next;
    }), []);

    // Check session on mount via /auth/me
    useEffect(() => {
        let cancelled = false;
        apiFetch('/auth/me')
            .then(res => res.json())
            .then(data => {
                if (cancelled) return;
                setUsername(data.username);
                setStoredUsername(data.username);
                setIsAuthenticated(true);
            })
            .catch(() => {
                if (!cancelled) setIsAuthenticated(false);
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const handleAuthLogout = () => {
            setStoredUsername(null);
            setStoredUserId(null);
            setIsAuthenticated(false);
            setUsername('');
        };
        window.addEventListener('auth:logout', handleAuthLogout);
        return () => window.removeEventListener('auth:logout', handleAuthLogout);
    }, []);

    const handleLogin = useCallback((loggedInUsername, userId) => {
        setStoredUsername(loggedInUsername);
        setStoredUserId(userId);
        setUsername(loggedInUsername);
        setIsAuthenticated(true);
    }, []);

    const handleLogout = useCallback(async () => {
        try { await apiFetch('/auth/logout', {method: 'POST'}); } catch {}
        setStoredUsername(null);
        setStoredUserId(null);
        setIsAuthenticated(false);
        setUsername('');
    }, []);

    const handleDeleteAccount = useCallback(async () => {
        await apiFetch('/users/me', {method: 'DELETE'});
        setStoredUsername(null);
        setStoredUserId(null);
        setIsAuthenticated(false);
        setUsername('');
    }, []);

    if (isAuthenticated === null) {
        return <Spinner size={32} className="spinner-fullpage" />;
    }

    if (!isAuthenticated) {
        const params = new URLSearchParams(window.location.search);
        const wantsLogin = params.has('signin');
        const wantsRegister = params.has('register');
        const wantsForgotPassword = params.has('forgot-password');
        const wantsResetPassword = params.has('reset-password');
        const verifiedStatus = params.get('verified') || null;
        const resetStatus = params.get('reset') || null;
        if (!wantsLogin && !wantsRegister && !wantsForgotPassword && !wantsResetPassword && !verifiedStatus && !resetStatus) {
            return <LandingPage theme={theme} onToggleTheme={toggleTheme}/>;
        }
        return (
            <div className="App" data-theme={theme}>
                <ToastProvider>
                    <Header theme={theme} onToggleTheme={toggleTheme} onLogoClick={() => { window.location.href = '/'; }}/>
                    {wantsRegister
                        ? <RegisterScreen/>
                        : wantsForgotPassword
                        ? <ForgotPasswordScreen/>
                        : wantsResetPassword
                        ? <ResetPasswordScreen/>
                        : <LoginScreen onLogin={handleLogin} verifiedStatus={verifiedStatus} resetStatus={resetStatus}/>
                    }
                    <Footer/>
                </ToastProvider>
            </div>
        );
    }

    return (
        <div className="App" data-theme={theme}>
            <ToastProvider>
                <Header
                    theme={theme}
                    onToggleTheme={toggleTheme}
                    onLogout={handleLogout}
                    username={username}
                    onAccountSettings={() => { setSidebarOpen(false); setView('settings'); }}
                    onLogoClick={() => setView('checklists')}
                    onToggleSidebar={toggleSidebar}
                    sidebarOpen={sidebarOpen}
                    currentViewLabel={currentViewLabel}
                />
                {view === 'settings' ? (
                    <AccountSettingsPage
                        username={username}
                        onDeleteAccount={handleDeleteAccount}
                        onBack={() => setView('checklists')}
                    />
                ) : (
                    <ChecklistContainer sidebarOpen={sidebarOpen} onViewLabelChange={setCurrentViewLabel}/>
                )}
                <Footer/>
            </ToastProvider>
        </div>
    );
}
